import { useEffect, useState } from "react";
import { api } from "./lib/api";

// ── SALES TAX, TAXED WHEN CREDIT IS SPENT (0375) ──────────────────────────────────────────────────
//
// Caleb, 2026-10-25: credit pays for things taxed differently, so buying credit is an untaxed deposit
// and each spend carries its own tax. Nothing is taxed until the start date (the permit's). The rate,
// each item's taxable share, and any campaign's own rate are set here, because the Comptroller's
// answer may change them; the report adds up what a Texas return asks for, for any date range.

type Setting = { starts_on: string | null; default_rate: number; permit_number: string | null;
  items: { item: string; description: string; taxable_pct: number }[];
  campaigns: { campaign_id: string; name: string; rate: number | null; exempt: string | null }[] };
type Report = { from: string; to: string; sales_cents: number; taxable_cents: number; tax_cents: number; tax_due_cents: number;
  by_rate: { rate: number; taxable_cents: number; tax_cents: number }[];
  by_campaign: { name: string; sales_cents: number; taxable_cents: number; tax_cents: number }[] };

const $ = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (r: number) => `${+(r * 100).toFixed(3)}%`;

/** This quarter and last, as [first day, last day] in Central time. */
function quarters(): { label: string; from: string; to: string }[] {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }));
  const out = [];
  for (let back = 0; back < 2; back++) {
    const q0 = Math.floor(now.getMonth() / 3) - back;
    const y = now.getFullYear() + Math.floor(q0 / 4);
    const q = ((q0 % 4) + 4) % 4;
    const from = new Date(y, q * 3, 1), to = new Date(y, q * 3 + 3, 0);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push({ label: `${back ? "Last" : "This"} quarter (Q${q + 1} ${y})`, from: iso(from), to: iso(to) });
  }
  return out;
}

export function SalesTax() {
  const [s, setS] = useState<Setting | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => api<Setting>("/staff/sales-tax").then((x) => { setS(x); setErr(null); }).catch((e) => setErr(String(e?.message ?? e)));
  useEffect(() => { load(); }, []);
  if (err) return <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm">Could not load sales tax: {err}</div>;
  if (!s) return null;
  return (
    <div className="rounded-xl border border-border px-4 py-3 text-sm grid gap-3">
      <div>
        <div className="font-bold mb-0.5">Sales tax</div>
        <div className="text-muted">
          Every price includes the tax: credit goes down by exactly the listed price, and the tax inside each spend is worked out
          (price ÷ (1 + taxable share × rate)) and kept for the return. Buying credit isn't taxed.{" "}
          {s.starts_on ? <>Collecting from <b className="text-text">{s.starts_on}</b>.</> : <b className="text-text">Not collecting yet.</b>}
        </div>
      </div>
      <Settings s={s} onSaved={load} />
      <ReportCard />
    </div>
  );
}

function Settings({ s, onSaved }: { s: Setting; onSaved: () => void }) {
  const [start, setStart] = useState(s.starts_on ?? "");
  const [rate, setRate] = useState(String(+(s.default_rate * 100).toFixed(4)));
  const [permit, setPermit] = useState(s.permit_number ?? "");
  const [items, setItems] = useState(s.items.map((i) => ({ ...i, v: String(i.taxable_pct) })));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function save() {
    setBusy(true); setMsg(null);
    try {
      await api("/staff/sales-tax", { method: "POST", body: JSON.stringify({
        starts_on: start || null, rate_pct: parseFloat(rate), permit_number: permit,
        items: items.map((i) => ({ item: i.item, taxable_pct: parseFloat(i.v) })) }) });
      setMsg("Saved."); onSaved();
    } catch (e: any) { setMsg(String(e?.message ?? e)); } finally { setBusy(false); }
  }
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs">Start collecting on<input type="date" className="ctl" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="flex flex-col text-xs">Default rate (%)<input className="ctl" style={{ width: 90 }} value={rate} onChange={(e) => setRate(e.target.value)} /></label>
        <label className="flex flex-col text-xs">Permit number<input className="ctl" style={{ width: 170 }} value={permit} onChange={(e) => setPermit(e.target.value)} /></label>
      </div>
      <table className="w-full max-w-xl">
        <thead><tr className="text-left text-muted text-xs uppercase"><th className="py-1">Item</th><th className="text-right">Taxable share (%)</th></tr></thead>
        <tbody>{items.map((i, k) => (
          <tr key={i.item} className="border-t border-border">
            <td className="py-1">{i.description}</td>
            <td className="text-right"><input className="ctl text-right" style={{ width: 80 }} value={i.v}
              onChange={(e) => setItems(items.map((x, j) => j === k ? { ...x, v: e.target.value } : x))} /></td>
          </tr>
        ))}</tbody>
      </table>
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={busy} className="btn disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        {msg && <span className={msg === "Saved." ? "text-green-700" : "text-red-700"}>{msg}</span>}
      </div>
      {s.campaigns.length > 0 && (
        <div className="text-xs text-muted">
          Campaigns with their own rate or exemption (set on each campaign's Credit tab):{" "}
          {s.campaigns.map((c) => `${c.name} (${c.exempt ? `exempt: ${c.exempt}` : pct(c.rate ?? 0)})`).join("; ")}
        </div>
      )}
    </div>
  );
}

function ReportCard() {
  const qs = quarters();
  const [from, setFrom] = useState(qs[1].from);
  const [to, setTo] = useState(qs[1].to);
  const [r, setR] = useState<Report | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setErr(null);
    api<Report>(`/staff/sales-tax/report?start=${from}&end=${to}`).then(setR).catch((e) => setErr(String(e?.message ?? e)));
  }, [from, to]);
  return (
    <div className="grid gap-2 border-t border-border pt-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="font-bold">For a return</span>
        {qs.map((q) => (
          <button key={q.label} onClick={() => { setFrom(q.from); setTo(q.to); }}
            className={`text-xs underline ${from === q.from && to === q.to ? "font-bold" : ""}`}>{q.label}</button>
        ))}
        <label className="flex flex-col text-xs">From<input type="date" className="ctl" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="flex flex-col text-xs">To<input type="date" className="ctl" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      {err && <div className="err">{err}</div>}
      {r && (
        <table className="w-full max-w-xl"><tbody>
          <tr><td className="text-muted">Total sales, before tax (credit spent on anything)</td><td className="text-right">{$(r.sales_cents)}</td></tr>
          <tr><td className="text-muted">Taxable sales</td><td className="text-right">{$(r.taxable_cents)}</td></tr>
          {r.by_rate.map((b) => (
            <tr key={b.rate}><td className="text-muted pl-4">at {pct(b.rate)}</td><td className="text-right">{$(b.taxable_cents)} → {$(b.tax_cents)} tax</td></tr>
          ))}
          <tr className="font-bold"><td>Tax collected</td><td className="text-right">{$(r.tax_due_cents)}</td></tr>
        </tbody></table>
      )}
      {r && r.by_campaign.length > 0 && (
        <table className="w-full max-w-xl text-xs"><tbody>
          {r.by_campaign.map((c) => (
            <tr key={c.name} className="border-t border-border"><td className="py-1">{c.name}</td>
              <td className="text-right">{$(c.sales_cents)} sales</td><td className="text-right">{$(c.tax_cents)} tax</td></tr>
          ))}
        </tbody></table>
      )}
    </div>
  );
}
